/**
 * Shared types + helpers for the Report DSL used across the Settings
 * (SuiteReportEditor) and Reports (ReportPanel, Reports page) modules.
 *
 * Keep this in sync with `st-ck-server/src/db/modules/infralytiqs/models/ReportQueryBuilder.ts`.
 */

export type AggregationFn =
  | 'count' | 'uniqExact'
  | 'sum' | 'avg' | 'min' | 'max'
  /** Value from the row with the latest event_time in the filtered set. */
  | 'argMax';

export type FilterOp =
  | 'eq' | 'neq' | 'in' | 'nin'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'contains' | 'not_contains'
  | 'starts_with' | 'not_starts_with'
  | 'exists' | 'not_exists'
  /** Field holds a JSON string array; matches when any element starts with `value`. */
  | 'array_starts_with'
  /** Field holds a comma-separated list; matches when any element starts with `value`. */
  | 'csv_any_starts_with';

export type TimeRangePreset =
  | 'last_1_hour' | 'last_6_hours' | 'last_12_hours' | 'last_24_hours' | 'last_7_days'
  | 'last_30_days' | 'last_90_days' | 'last_180_days' | 'last_365_days'
  | 'today' | 'yesterday' | 'this_week' | 'this_month' | 'this_year';

export type Granularity = 'minute' | 'hour' | 'day' | 'week' | 'month';

export type VisualizationType =
  | 'line' | 'area' | 'bar'
  | 'pie' | 'donut'
  | 'gauge' | 'table' | 'number'
  /**
   * Geographic heat-map rendered on Google Maps, driven by a single
   * dimension that must be an ISO-3166 alpha-2 country code
   * (matches the `country_code` column on `website_events`).
   */
  | 'geoheat'
  /**
   * Compact "counter" tile (big number + label) intended for `mode: toolbar`
   * panels. Renders inline in the Reports toolbar next to the suite/time
   * pickers, e.g. "Unique Users — last 30 days = 1,287".
   */
  | 'counter'
  /**
   * Realtime line chart with a moving x-axis, polling for live data on a
   * fixed cadence (default every 10 seconds). Bucket count adapts to the
   * viewport width — wider screens render more time intervals on the
   * x-axis. Used for Google-Analytics-style "live users" panels.
   */
  | 'realtime'
  /**
   * Same panel + polling behavior as `realtime`, but renders as a bar
   * chart instead of a line. Useful for "right now" emphasis where each
   * bucket reads as a discrete count rather than a trend line. The
   * realtime panel honors the `vis.options.chartType` override too, but
   * exposing this as a first-class type makes "Realtime Bar (live users)"
   * a one-click pick from the visualization dropdown.
   */
  | 'realtime_bar'
  /**
   * Horizontal color-coded storage quota bar for a single DAM folder.
   * Options: `damFolderPath`, `maxStorageTb`, `valueFormat: 'decimal_mb'`.
   */
  | 'storage_quota_bar'
  /**
   * Stacked horizontal quota bar across multiple DAM folders (each with its own max TB).
   * Options: `damFolderQuotas: { path, maxStorageTb? }[]`, `valueFormat: 'decimal_mb'`.
   * When `maxStorageTb` is omitted, current used storage is treated as the folder max.
   */
  | 'storage_quota_stacked_bar'
  /**
   * Read-only "detail card" that renders a single matched event as a list
   * of label : value rows. Intended for `mode: 'drilldown'` reports opened
   * from a table row click (e.g. a per-search-event detail), where the
   * runtime filter (typically `event_id = <clicked row>`) narrows the query
   * to exactly one row. Special per-field formatting (links, enabled /
   * disabled flags, filter JSON) is driven by `vis.options.columnDisplay`.
   */
  | 'detail_card';

export interface DamFolderQuotaEntry {
  path: string;
  /** When omitted or ≤ 0, the panel treats current used storage as the effective max. */
  maxStorageTb?: number;
}

export interface MetricDef {
  field: string;
  fn: AggregationFn;
  label?: string;
}

export interface DimensionDef {
  field: string;
  label?: string;
  /**
   * Treat a `custom_dimensions.*` value as a JSON string array and expand it
   * into one row per element (server-side `arrayJoin`). Used for list-valued
   * dimensions such as `user_groups` (`["a","b",...]`).
   */
  jsonArray?: {
    /** Keep only elements starting with this (case-sensitive) prefix. */
    prefix?: string;
  };
}

/**
 * Table UI-only: how a column is rendered (CSV keeps raw values).
 *   - 'url_actions' / 'comma_multiline': dimension columns.
 *   - 'bytes_mb': metric column holding a raw byte count, rendered in MB.
 *   - 'duration_min': metric column holding milliseconds, rendered in minutes.
 *   - 'count_capped_100': integer count; values ≥ 100 render as "100+".
 *   - 'link_click_here': a URL rendered as a "Click Here" hyperlink
 *     (new tab, noopener) instead of the raw URL string.
 *   - 'filters_json': a JSON string (object or array of filter objects)
 *     rendered as a compact, human-readable list. Empty / unparseable
 *     values render as "None".
 *   - 'json_array_multiline': a JSON string holding an array of strings
 *     (e.g. asset paths) rendered one item per line.
 *   - 'enabled_disabled': a boolean-ish value ('true'/'false'/1/0)
 *     rendered as "Enabled" / "Disabled".
 */
export type TableColumnDisplayAs =
  | 'url_actions'
  | 'comma_multiline'
  | 'bytes_mb'
  | 'duration_min'
  | 'count_capped_100'
  | 'link_click_here'
  | 'filters_json'
  | 'json_array_multiline'
  | 'enabled_disabled';

export interface FilterDef {
  field: string;
  op: FilterOp;
  value?: string | number | boolean | Array<string | number>;
}

/**
 * A single projected output column for a {@link UnionSourceDef}. Maps a source
 * event field onto a shared output column name (`as`), optionally exploding a
 * JSON string array into one row per element (same semantics as
 * {@link DimensionDef.jsonArray}).
 */
export interface UnionProjectionDef {
  /** Output column name. Safe identifier; matches an outer dimension `field`. */
  as: string;
  /** Source field (whitelisted column or `custom_dimensions.*` / `custom_metrics.*`). */
  field: string;
  /** Explode a JSON string array into one row per element via `arrayJoin`. */
  jsonArray?: {
    /** Keep only elements starting with this (case-sensitive) prefix. */
    prefix?: string;
    /**
     * De-duplicate array elements (after `decode`) before exploding, so the
     * same value appearing twice in one event's array counts once.
     */
    distinct?: boolean;
  };
  /**
   * Wrap the value in `decodeURLComponent(...)` so percent-encoded and plain
   * forms of the same value merge into one group.
   */
  decode?: boolean;
}

/**
 * One branch of a generic `UNION ALL`. Each source filters the events table
 * independently and projects the SAME set of output columns (`project[].as`),
 * possibly from different source fields. See {@link ReportQueryDSL.union}.
 */
export interface UnionSourceDef {
  /** Per-source WHERE filters (e.g. `event_type = 'cart_download'`). */
  filters?: FilterDef[];
  /** Projection list — every source must declare the SAME set of `as` names. */
  project: UnionProjectionDef[];
}

export interface TimeRangeDef {
  preset?: TimeRangePreset;
  start?: string;
  end?: string;
  granularity?: Granularity;
}

export interface ReportQueryDSL {
  metrics: MetricDef[];
  dimensions?: DimensionDef[];
  filters?: FilterDef[];
  timeRange?: TimeRangeDef;
  orderBy?: Array<{ field: string; dir?: 'ASC' | 'DESC' }>;
  limit?: number;
  offset?: number;
  /**
   * Generic `UNION ALL` of heterogeneous event structures into one row set,
   * then grouped/aggregated by the outer `dimensions` / `metrics`. Each source
   * projects the same output columns; outer dimension/filter/orderBy field
   * references resolve against those projected column names. Tenant/site/time
   * predicates are applied inside each source by the server.
   */
  union?: UnionSourceDef[];
}

export interface VisualizationDef {
  type: VisualizationType;
  title?: string;
  /**
   * Free-form renderer options. Common keys:
   *   - `dimensionValueLabels`: map of raw dimension value → legend / slice
   *     text for pie, donut, and bar charts. Example:
   *     `{ "lucidlink_asset_migration_skipped": "Unchanged" }`.
   *     Matching is case-insensitive. Unmapped values keep the default
   *     pretty-print (and any built-in field overrides).
   */
  options?: Record<string, any>;
}

export interface InteractionDef {
  onRowClick?: {
    targetReportName?: string;
    targetReportId?: string;
    passField: string;
    /**
     * Sibling level-2 reports to open on a row click, in this order.
     * - When `passField === 'user_id'`: restricts the per-user page.
     * - For any other `passField` (e.g. `custom_dimensions.run_id`): the
     *   drill-down page stacks these tables, all filtered by `passField`.
     * When omitted, a single `targetReportName` / `targetReportId` is used
     * (or every drill-down report on the per-user page).
     */
    level2ReportNames?: string[];
  };
  /** Opens a drill-down report on its own hash URL (e.g. counter → env tiles). */
  onPanelClick?: {
    targetReportName?: string;
    targetReportId?: string;
    /**
     * Sibling drill-down reports shown together on the panel-click page,
     * in this order (e.g. Production / Stage / SIT / QA / DEV counters).
     * When omitted, only `targetReportName` / `targetReportId` is shown.
     */
    level2ReportNames?: string[];
  };
}

export type ReportMode = 'dashboard' | 'drilldown' | 'toolbar';

export interface ReportDefinition {
  _id?: string;
  name: string;
  description?: string;
  /**
   * Where the panel renders on the Reports page.
   *   - 'dashboard' (default): main grid.
   *   - 'drilldown': hidden until a row click navigates to it.
   *   - 'toolbar': compact counter tile rendered next to the time-range
   *     picker on the Reports toolbar (use with `visualizations[0].type =
   *     'counter'`).
   */
  mode?: ReportMode;
  layout?: { x: number; y: number; w: number; h: number };
  query: ReportQueryDSL;
  visualizations: VisualizationDef[];
  interactions?: InteractionDef;
  rangeComparison?: { enabled: boolean; presets?: TimeRangePreset[] };
  createdAt?: string;
  updatedAt?: string;
}

export interface ReportSuiteDetail {
  _id: string;
  name: string;
  description?: string;
  timezone?: string;
  defaultTimeRange?: TimeRangeDef;
  savedMetrics?: MetricDef[];
  savedSegments?: Array<{ id: string; name: string; filters: FilterDef[] }>;
  reports: ReportDefinition[];
  createdAt?: string;
  updatedAt?: string;
}

// ─── Catalog of columns the user can pick in the wizard ───────────────────

export interface FieldOption {
  field: string;
  label: string;
  group: 'Identity' | 'Event' | 'Page' | 'Device' | 'Marketing' | 'Time' | 'Custom';
  allowedForDimension?: boolean;
  allowedForMetric?: boolean;
  isNumeric?: boolean;
}

export const FIELD_CATALOG: FieldOption[] = [
  { field: 'user_id', label: 'User ID', group: 'Identity', allowedForDimension: true, allowedForMetric: true },
  { field: 'anonymous_id', label: 'Anonymous ID', group: 'Identity', allowedForDimension: true, allowedForMetric: true },
  { field: 'session_id', label: 'Session ID', group: 'Identity', allowedForDimension: true, allowedForMetric: true },
  { field: 'event_type', label: 'Event Type', group: 'Event', allowedForDimension: true, allowedForMetric: true },
  { field: 'event_subtype', label: 'Event Subtype', group: 'Event', allowedForDimension: true, allowedForMetric: true },
  { field: 'event_id', label: 'Event ID', group: 'Event', allowedForDimension: false, allowedForMetric: true },
  { field: 'event_time', label: 'Event Time', group: 'Time', allowedForDimension: true, allowedForMetric: false },
  { field: 'event_date', label: 'Event Date', group: 'Time', allowedForDimension: true, allowedForMetric: false },
  { field: 'page_url', label: 'Page URL', group: 'Page', allowedForDimension: true, allowedForMetric: true },
  { field: 'page_path', label: 'Page Path', group: 'Page', allowedForDimension: true, allowedForMetric: true },
  { field: 'asset_id', label: 'Asset ID', group: 'Page', allowedForDimension: true, allowedForMetric: true },
  { field: 'search_term', label: 'Search Term', group: 'Page', allowedForDimension: true, allowedForMetric: true },
  { field: 'country_code', label: 'Country', group: 'Device', allowedForDimension: true, allowedForMetric: true },
  { field: 'device_type', label: 'Device Type', group: 'Device', allowedForDimension: true, allowedForMetric: true },
  { field: 'device_os', label: 'Device OS', group: 'Device', allowedForDimension: true, allowedForMetric: true },
  { field: 'language_iso_code', label: 'Language', group: 'Device', allowedForDimension: true, allowedForMetric: true },
  { field: 'utm_source', label: 'UTM Source', group: 'Marketing', allowedForDimension: true, allowedForMetric: true },
];

export const AGG_OPTIONS: Array<{ value: AggregationFn; label: string; needsField: boolean }> = [
  { value: 'count', label: 'Count (all)', needsField: false },
  { value: 'uniqExact', label: 'Unique (exact)', needsField: true },
  { value: 'sum', label: 'Sum', needsField: true },
  { value: 'avg', label: 'Average', needsField: true },
  { value: 'min', label: 'Min', needsField: true },
  { value: 'max', label: 'Max', needsField: true },
  { value: 'argMax', label: 'Latest value (by event time)', needsField: true },
];

export const OP_OPTIONS: Array<{ value: FilterOp; label: string; arity: 'single' | 'multi' | 'none' }> = [
  { value: 'eq', label: 'equals', arity: 'single' },
  { value: 'neq', label: 'not equals', arity: 'single' },
  { value: 'in', label: 'in (list)', arity: 'multi' },
  { value: 'nin', label: 'not in (list)', arity: 'multi' },
  { value: 'contains', label: 'contains', arity: 'single' },
  { value: 'not_contains', label: 'does not contain', arity: 'single' },
  { value: 'gt', label: '>', arity: 'single' },
  { value: 'gte', label: '>=', arity: 'single' },
  { value: 'lt', label: '<', arity: 'single' },
  { value: 'lte', label: '<=', arity: 'single' },
  { value: 'exists', label: 'is set', arity: 'none' },
  { value: 'not_exists', label: 'is not set', arity: 'none' },
  { value: 'array_starts_with', label: 'array element starts with', arity: 'single' },
  { value: 'csv_any_starts_with', label: 'comma-list element starts with', arity: 'single' },
];

export const TIME_RANGE_OPTIONS: Array<{ value: TimeRangePreset; label: string }> = [
  { value: 'last_1_hour', label: 'Last 1 hour' },
  { value: 'last_6_hours', label: 'Last 6 hours' },
  { value: 'last_12_hours', label: 'Last 12 hours' },
  { value: 'last_24_hours', label: 'Last 24 hours' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'last_180_days', label: 'Last 180 days' },
  { value: 'last_365_days', label: 'Last 365 days' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'this_year', label: 'This year' },
];

export const GRANULARITY_OPTIONS: Array<{ value: Granularity; label: string }> = [
  { value: 'minute', label: 'Minute' },
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

/**
 * Predefined width buckets shown in the editor's "Width" picker.
 *
 * Stored on `report.layout.w` as a 1..12 column span (matches the 12-column
 * CSS grid used by `Reports.tsx` and `UserDrillDownPage.tsx`). The Reports
 * dashboard clamps anything outside [3..12], so values below 3 are forced
 * back to a quarter-width on render — keeping that rule colocated with the
 * UI options here so the two never drift.
 */
export const LAYOUT_WIDTH_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 12, label: 'Full width (12 / 12)' },
  { value: 8,  label: 'Two-thirds (8 / 12)' },
  { value: 6,  label: 'Half width (6 / 12)' },
  { value: 4,  label: 'One-third (4 / 12)' },
  { value: 3,  label: 'Quarter (3 / 12)' },
];

/**
 * Hard bounds on `layout.w` and `layout.h` enforced by the dashboard renderer
 * and the resize handle. Shared between the editor (so the form clamps user
 * input) and `ReportPanelResizeWrapper` (so drag snap-points stay in sync).
 *
 *   - `w` is in 12-column grid units. Below 3 most panels become unreadable on
 *     desktop; above 12 it would overflow the grid track.
 *   - `h` is in dashboard row units (1 row ≈ REPORT_PANEL_ROW_PX = 80 px). The
 *     ceiling keeps a single panel from monopolising the viewport.
 */
export const LAYOUT_MIN_W = 3;
export const LAYOUT_MAX_W = 12;
export const LAYOUT_MIN_H = 3;
export const LAYOUT_MAX_H = 40;

/**
 * Visualization types that stay legible at 2 columns, so they're allowed one
 * column narrower than the general minimum. Counters (single number) and the
 * vertical storage-quota bar are compact by design. Richer panels (charts,
 * tables, maps) keep `LAYOUT_MIN_W` to remain readable.
 */
export const LAYOUT_MIN_W_NARROW = 2;
/** @deprecated use LAYOUT_MIN_W_NARROW */
export const LAYOUT_MIN_W_COUNTER = LAYOUT_MIN_W_NARROW;

const NARROW_VIS_TYPES = new Set<string>(['counter', 'storage_quota_bar']);

/** Resolve the minimum allowed `layout.w` for a report based on its visual type. */
export function layoutMinWidthForReport(
  report: { visualizations?: Array<{ type?: string }> } | null | undefined,
): number {
  const type = report?.visualizations?.[0]?.type;
  return type && NARROW_VIS_TYPES.has(type) ? LAYOUT_MIN_W_NARROW : LAYOUT_MIN_W;
}

/**
 * Returns reports ordered for display.
 *
 * Primary key:  `layout.y`  (the explicit "Display order" the user set in
 *               the editor — lower values render first).
 * Tiebreaker:   original array index, which preserves insertion order for
 *               legacy reports that don't have a y value yet.
 *
 * `Array.prototype.sort` is stable in all modern JS engines (V8, JSC,
 * SpiderMonkey since 2019) so we don't need to materialize the index pair
 * — but we do anyway for clarity and to make the intent obvious to anyone
 * reading dashboard order surprises in the future.
 */
export const sortReportsByLayout = <T extends { layout?: { y?: number } }>(
  reports: T[],
): T[] => {
  return reports
    .map((r, idx) => ({ r, idx, y: typeof r.layout?.y === 'number' ? r.layout!.y! : Number.POSITIVE_INFINITY }))
    .sort((a, b) => (a.y - b.y) || (a.idx - b.idx))
    .map((x) => x.r);
};

export const VISUALIZATION_OPTIONS: Array<{ value: VisualizationType; label: string }> = [
  { value: 'line', label: 'Line Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'bar', label: 'Bar Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'donut', label: 'Donut Chart' },
  { value: 'gauge', label: 'Gauge' },
  { value: 'table', label: 'Table' },
  { value: 'number', label: 'Single Number' },
  { value: 'geoheat', label: 'Map Heatmap (Google Maps)' },
  { value: 'counter', label: 'Counter Tile (toolbar)' },
  { value: 'realtime', label: 'Realtime Line (live users)' },
  { value: 'realtime_bar', label: 'Realtime Bar (live users)' },
  { value: 'storage_quota_bar', label: 'DAM Storage Quota Bar' },
  { value: 'storage_quota_stacked_bar', label: 'DAM Multi-Folder Storage Quota Bar' },
];

/** Counter tile icon options — stored on `visualizations[0].options.icon`. */
export const COUNTER_ICON_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'auto', label: 'Auto (from metric field)' },
  { value: 'groups2', label: 'Users / groups' },
  { value: 'person', label: 'Person / visitor' },
  { value: 'session', label: 'Session / schedule' },
  { value: 'pageview', label: 'Page / visibility' },
  { value: 'insights', label: 'Insights' },
  { value: 'share', label: 'Share' },
  { value: 'download', label: 'Download' },
  { value: 'storage', label: 'Hard disk' },
  { value: 'none', label: 'No icon' },
];

export const REPORT_MODE_OPTIONS: Array<{ value: ReportMode; label: string; description: string }> = [
  { value: 'dashboard', label: 'Dashboard', description: 'Shown by default in the main reports grid.' },
  { value: 'drilldown', label: 'Drill-down only', description: 'Hidden until a row click navigates to it.' },
  { value: 'toolbar', label: 'Toolbar tile', description: 'Compact counter tile next to the toolbar pickers (use with the Counter Tile visualization).' },
];

/**
 * Visualization types that aggregate a single total per dimension slice —
 * no time bucketing makes sense for them. Used to strip `granularity`
 * when switching the wizard to one of these viz types.
 *
 * `realtime` is also listed here because the realtime panel synthesizes its
 * own bucketing client-side from `vis.options.bucketSeconds`, so the DSL
 * `granularity` field is irrelevant for it.
 */
const NON_TIME_BUCKETED_VIZ: ReadonlySet<VisualizationType> = new Set(
  ['pie', 'donut', 'gauge', 'number', 'geoheat', 'counter', 'realtime', 'realtime_bar', 'storage_quota_bar', 'storage_quota_stacked_bar'],
);

/** Returns a minimal, valid starter DSL for new reports. */
export const emptyReport = (visualization: VisualizationType = 'line'): ReportDefinition => ({
  name: '',
  description: '',
  mode: 'dashboard',
  layout: { x: 0, y: 0, w: 12, h: 4 },
  query: {
    metrics: [{ field: '*', fn: 'count', label: 'Events' }],
    dimensions: [],
    filters: [],
    timeRange: {
      preset: 'last_30_days',
      granularity: NON_TIME_BUCKETED_VIZ.has(visualization) ? undefined : 'day',
    },
    limit: 1000,
  },
  visualizations: [{ type: visualization, title: '' }],
  interactions: {},
  rangeComparison: { enabled: false },
});
